import {
    BinaryOpType,
    CompareOpType,
    IFormulaNode
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaDefinitions';
import {
    BaseFormulaVisitor,
    IFormulaVisitor,
    IFormulaVisitorAsync,
    ShouldContinueTraversal
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaVisitor';
import {SheetLocationImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetLocationImpl';
import {
    ColumnIndex,
    RowIndex,
    SlotGlobalKey,
    SlotLocation
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {ISheetLocation} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import bind from 'bind-decorator';

export abstract class FormulaExpression implements IFormulaNode
{
    public abstract acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>;

    public abstract accept(visitor:IFormulaVisitor):void;

    public abstract toString():string;
}

export class FormulaValueExpression extends FormulaExpression
{
    public constructor(public readonly expr:IFormulaNode, public readonly accumulateResult:boolean = false)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        await visitor.preFormulaExpr(this);
        await this.expr.acceptAsync(visitor);
        return await visitor.formulaExpr(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.preFormulaExpr(this);
        this.expr.accept(visitor);
        visitor.postFormulaExpr(this);
    }

    public toString():string
    {
        if( this.accumulateResult )
            return `+=${this.expr.toString()}`;
        else
            return `=${this.expr.toString()}`;
    }
}

export class LazyFormulaExpression extends FormulaExpression
{
    public constructor(public readonly expr:IFormulaNode, public readonly accumulateResult:boolean = false)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        await visitor.preLazyExpr(this);
        await this.expr.acceptAsync(visitor);
        return await visitor.lazyExpr(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.preLazyExpr(this);
        this.expr.accept(visitor);
        visitor.postLazyExpr(this);
    }

    public toString():string
    {
        if( this.accumulateResult )
            return `+=?${this.expr.toString()}`;
        else
            return `=?${this.expr.toString()}`;
    }
}

export class LiveFormulaExpression extends FormulaExpression
{
    public constructor(public readonly expr:IFormulaNode, public readonly accumulateResult:boolean = false)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        await visitor.preLiveExpr(this);
        await this.expr.acceptAsync(visitor);
        return await visitor.liveExpr(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.preLiveExpr(this);
        this.expr.accept(visitor);
        visitor.postLiveExpr(this);
    }

    public toString():string
    {
        if( this.accumulateResult )
            return `+=*${this.expr.toString()}`;
        else
            return `=*${this.expr.toString()}`;
    }
}

export class ValueLiteral extends FormulaExpression
{
    public constructor(public readonly value:IValue | null)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        return await visitor.valueLiteral(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.valueLiteral(this);
    }

    public toString():string
    {
        if( this.value == null )
            return 'null';

        return `${this.value} (${this.value.getType().kind})`;
    }
}

export class IdentifierLiteral extends FormulaExpression
{
    public constructor(public readonly identifier:AString)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        return await visitor.identifierLiteral(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.identifierLiteral(this);
    }

    public toString():string
    {
        return `${this.identifier}`;
    }
}

export class CellReference implements IFormulaNode
{
    constructor(public readonly sheetId:SheetId,
                public readonly colIndex:ColumnIndex,
                public readonly rowIndex:RowIndex)
    {
    }

    get globalKey():SlotGlobalKey
    {
        return this.toSlotLocation().toGlobalKey(this.sheetId);
    }

    public toSlotLocation():SlotLocation
    {
        return new SlotLocation(this.colIndex, this.rowIndex);
    }

    public toSheetLocation():ISheetLocation
    {
        return new SheetLocationImpl(this.sheetId, this.colIndex, this.rowIndex);
    }

    @bind
    public updateToExternalRef(otherSheetId:SheetId):void
    {
        this['' + 'sheetId'] = otherSheetId;
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        return await visitor.cellReference(this);
    }

    public accept(visitor:IFormulaVisitor)
    {
        visitor.cellReference(this);
    }

    public toString()
    {
        return this.toSlotLocation().toA1String();
    }
}

class UpdateToExternalRefVisitor extends BaseFormulaVisitor
{
    constructor(public readonly otherSheetId:SheetId)
    {
        super();
    }

    cellReference(cellRef:CellReference)
    {
        cellRef.updateToExternalRef(this.otherSheetId);
    }
}

export class RangeReference extends FormulaExpression
{
    public constructor(public readonly from:IFormulaNode,
                       public readonly to:IFormulaNode)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        await this.from.acceptAsync(visitor);
        await this.to.acceptAsync(visitor);

        return await visitor.rangeReference(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        this.from.accept(visitor);
        this.to.accept(visitor);

        visitor.rangeReference(this);
    }

    @bind
    public updateToExternalRef(otherSheetId:SheetId):void
    {
        const visitor = new UpdateToExternalRefVisitor(otherSheetId);
        this.from.accept(visitor);
        this.to.accept(visitor);
    }

    public toString():string
    {
        const from = this.from.toString();
        const to = this.to.toString();

        return `${from}:${to}`;
    }
}

export class UnionReference extends FormulaExpression
{
    public constructor(public readonly left:IFormulaNode,
                       public readonly right:IFormulaNode)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        await this.left.acceptAsync(visitor);
        await this.right.acceptAsync(visitor);

        return await visitor.unionReference(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        this.left.accept(visitor);
        this.right.accept(visitor);

        visitor.unionReference(this);
    }

    public toString():string
    {
        const left = this.left.toString();
        const right = this.right.toString();

        return `${left}|${right}`;
    }
}

export class ListReference extends FormulaExpression
{
    public constructor(public readonly left:IFormulaNode,
                       public readonly right:IFormulaNode)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        await this.left.acceptAsync(visitor);
        await this.right.acceptAsync(visitor);

        return await visitor.listReference(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        this.left.accept(visitor);
        this.right.accept(visitor);

        visitor.listReference(this);
    }

    public toString():string
    {
        const left = this.left.toString();
        const right = this.right.toString();

        return `${left},${right}`;
    }
}

export class IntersectionReference extends FormulaExpression
{
    public constructor(public readonly left:IFormulaNode,
                       public readonly right:IFormulaNode)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        await this.left.acceptAsync(visitor);
        await this.right.acceptAsync(visitor);

        return await visitor.intersectionReference(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        this.left.accept(visitor);
        this.right.accept(visitor);

        visitor.intersectionReference(this);
    }

    public toString():string
    {
        const left = this.left.toString();
        const right = this.right.toString();

        return `${left} ${right}`;
    }
}

export class PercentExpr extends FormulaExpression
{
    public constructor(public readonly expr:IFormulaNode)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        await this.expr.acceptAsync(visitor);
        return await visitor.percentExpr(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.prePercentExpr(this);
        this.expr.accept(visitor);
        visitor.postPercentExpr(this);
    }

    public toString():string
    {
        return `${this.expr.toString()}%`;
    }
}

export class NegateExpr extends FormulaExpression
{
    public constructor(public readonly expr:IFormulaNode)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        await this.expr.acceptAsync(visitor);
        return await visitor.negateExpr(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.preNegateExpr(this);
        this.expr.accept(visitor);
        visitor.postNegateExpr(this);
    }

    public toString():string
    {
        return `-${this.expr.toString()}`;
    }
}

export class ParenExpr extends FormulaExpression
{
    public constructor(public readonly expr:IFormulaNode)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        visitor.preParenExpr(this);

        await this.expr.acceptAsync(visitor);
        await visitor.parenExpr(this);

        visitor.postParenExpr(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.preParenExpr(this);
        this.expr.accept(visitor);
        visitor.postParenExpr(this);
    }

    public toString():string
    {
        return `(${this.expr.toString()})`;
    }
}

export class BinaryExpr extends FormulaExpression
{
    public constructor(public readonly lhs:IFormulaNode,
                       public readonly rhs:IFormulaNode,
                       public readonly opType:BinaryOpType)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        await this.lhs.acceptAsync(visitor);
        await this.rhs.acceptAsync(visitor);

        return await visitor.binaryExpr(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.preBinaryExpr(this);

        this.lhs.accept(visitor);
        this.rhs.accept(visitor);

        visitor.postBinaryExpr(this);
    }

    public toString():string
    {
        return `${this.lhs.toString()} ${this.opType} ${this.rhs.toString()}`;
    }
}

export class ConcatExpr extends FormulaExpression
{
    public constructor(public readonly lhs:IFormulaNode, public readonly rhs:IFormulaNode)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        await this.lhs.acceptAsync(visitor);
        await this.rhs.acceptAsync(visitor);

        return await visitor.concatExpr(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.preConcatExpr(this);

        this.lhs.accept(visitor);
        this.rhs.accept(visitor);

        visitor.postConcatExpr(this);
    }

    public toString():string
    {
        return `${this.lhs.toString()}&${this.rhs.toString()}`;
    }
}

export class ComparisonExpr extends FormulaExpression
{
    public constructor(public readonly lhs:IFormulaNode,
                       public readonly rhs:IFormulaNode,
                       public readonly opType:CompareOpType)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        await this.lhs.acceptAsync(visitor);
        await this.rhs.acceptAsync(visitor);

        return await visitor.comparisonExpr(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.preComparisonExpr(this);

        this.lhs.accept(visitor);
        this.rhs.accept(visitor);

        visitor.postComparisonExpr(this);
    }

    public toString():string
    {
        return `${this.lhs.toString()}${this.opType}${this.rhs.toString()}`;
    }
}

export class InvokeFunctionExpr extends FormulaExpression
{
    public constructor(public readonly functionFullName:AString,
                       public readonly args:IFormulaNode[])
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        const shouldContinue:ShouldContinueTraversal = await visitor.preInvokeFunction(this);

        if( !shouldContinue )
            return;

        let paramIndex = 0;

        for( let arg of this.args )
        {
            await visitor.invokeFunctionParameter(this, arg, paramIndex);
            paramIndex++;
        }

        await visitor.invokeFunction(this);
        visitor.postInvokeFunction(this);

        return;
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.preInvokeFunction(this);

        for( let arg of this.args )
            arg.accept(visitor);

        visitor.postInvokeFunction(this);
    }

    public toString():string
    {
        return `${this.functionFullName}(${this.args.join(', ')})`;
    }
}

export class ArrayExpr extends FormulaExpression
{
    public constructor(public readonly rows:IFormulaNode[][])
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        for( let row of this.rows )
            for( let cell of row )
                await cell.acceptAsync(visitor);

        return await visitor.arrayExpr(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.preArrayExpr(this);

        for( let row of this.rows )
            for( let cell of row )
                cell.accept(visitor);

        visitor.postArrayExpr(this);
    }

    public toString():string
    {
        let rows:string[] = [];

        for( let row of this.rows )
            rows.push(row.join(','));

        return `{${rows.join(';')}}`;
    }
}

export class FrameColumnReference extends FormulaExpression
{
    public constructor(public readonly frameColumnName:AString)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        return await visitor.frameColumnReference(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.frameColumnReference(this);
    }

    public toString():string
    {
        return `@${this.frameColumnName}`;
    }
}

export class FrameCalculatedColumnExpr extends FormulaExpression
{
    public constructor(public readonly frameColumn:FrameColumnReference, public readonly expression:FormulaExpression)
    {
        super();
    }

    public async acceptAsync(visitor:IFormulaVisitorAsync):Promise<any>
    {
        return await visitor.frameCalculatedColumnExpr(this);
    }

    public accept(visitor:IFormulaVisitor):void
    {
        visitor.frameCalculatedColumnExpr(this);
    }

    public toString():string
    {
        return `${this.frameColumn} = ${this.expression}`;
    }
}
